import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Sheet, Spreadsheet } from '../../../../lib/Types'
import * as AccountingAPI from '../API/AccountingAPI'
import * as BudgetAPI from '../API/BudgetAPI'
import * as SheetAPI from '../API/SheetAPI'
import * as SpreadsheetAPI from '../API/SpreadsheetAPI'
import { SheetType } from '../Enums/SheetType'

export const useSheet = (): UseQueryResult<Sheet> => {
  const params = useParams()

  if (params.budgetId) {
    const budgetId = parseInt(params.budgetId, 10)

    return useQuery({
      queryKey: ['budgets', budgetId],
      queryFn: () => BudgetAPI.getBudget(budgetId),
      staleTime: Infinity,
    })
  } else if (params.accountingId) {
    const accountingId = parseInt(params.accountingId, 10)

    return useQuery({
      queryKey: ['accountings', accountingId],
      queryFn: () => AccountingAPI.getAccounting(accountingId),
      staleTime: Infinity,
    })
  } else if (params.spreadsheetId) {
    const spreadsheetId = parseInt(params.spreadsheetId, 10)

    return useQuery({
      queryKey: ['spreadsheets', spreadsheetId],
      queryFn: () => SpreadsheetAPI.getSpreadsheet(spreadsheetId),
      staleTime: Infinity,
    })
  }

  throw Error('Neither Budget nor Accounting ID was found in query parameters')
}

export const useUpdateSheet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sheet: Sheet) => {
      if (sheet.sheet_type === SheetType.SPREADSHEET) {
        return SpreadsheetAPI.saveSpreadsheet(sheet as Spreadsheet)
      } else {
        return SheetAPI.saveSheet(sheet)
      }
    },
    onMutate: (newSheet: Sheet): Sheet => {
      let queryKey = 'budgets'
      if (newSheet.sheet_type === SheetType.ACCOUNTING) {
        queryKey = 'accountings'
      }
      if (newSheet.sheet_type === SheetType.SPREADSHEET) {
        queryKey = 'spreadsheets'
      }

      queryClient.cancelQueries({ queryKey: [queryKey, newSheet.id] })

      const previousSheet = queryClient.getQueryData([
        queryKey,
        newSheet.id,
      ]) as Sheet

      queryClient.setQueryData([queryKey, newSheet.id], newSheet)

      return previousSheet
    },
    onError: (err, newSheet, previousSheet: Sheet | undefined) => {
      if (previousSheet) {
        let queryKey = 'budgets'
        if (newSheet.sheet_type === SheetType.ACCOUNTING) {
          queryKey = 'accountings'
        }
        if (newSheet.sheet_type === SheetType.SPREADSHEET) {
          queryKey = 'spreadsheets'
        }

        queryClient.setQueryData([queryKey, previousSheet.id], previousSheet)
      }

      console.error(err)
    },
    onSuccess: (data, newSheet: Sheet) => {
      const previousState = queryClient.getQueryData([
        newSheet.sheet_type,
      ]) as Sheet[]

      const newState = previousState ? [...previousState] : []

      newState.splice(
        previousState.findIndex((sheet: Sheet) => sheet.id === newSheet.id),
        1,
        newSheet,
      )

      queryClient.setQueryData([newSheet.sheet_type], newState)
      queryClient.setQueryData([newSheet.sheet_type, newSheet.id], newSheet)
    },
  })
}

export const useInviteUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sheet: Sheet) => SheetAPI.inviteUser(sheet),
    onMutate: (newSheet: Sheet): Sheet => {
      const queryKey =
        newSheet.sheet_type === SheetType.BUDGET
          ? ['budgets', newSheet.id]
          : ['accountings', newSheet.id]

      queryClient.cancelQueries({ queryKey })

      const previousSheet = queryClient.getQueryData(queryKey) as Sheet

      queryClient.setQueryData(queryKey, newSheet)

      return previousSheet
    },
    onError: (err, newSheet, previousSheet: Sheet | undefined) => {
      if (previousSheet) {
        const queryKey =
          previousSheet.sheet_type === SheetType.BUDGET
            ? ['budgets', previousSheet.id]
            : ['accountings', previousSheet.id]

        queryClient.setQueryData(queryKey, previousSheet)
      }

      console.error(err)
    },
  })
}

export const useDeleteSheet = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sheet: Sheet) => SheetAPI.deleteSheet(sheet),
    onSuccess: (data, sheet: Sheet) => {
      // Remove the delete sheet from the caches.
      if (
        sheet.sheet_type === SheetType.BUDGET ||
        sheet.sheet_type === SheetType.SPREADSHEET
      ) {
        queryClient.setQueryData(
          [sheet.sheet_type === SheetType.BUDGET ? 'budgets' : 'spreadsheets'],
          (current: Sheet[]) => [
            ...current.filter((currentSheet) => currentSheet.id !== sheet.id),
          ],
        )

        if (sheet.sheet_type === SheetType.BUDGET) {
          queryClient.invalidateQueries({ queryKey: ['task'] })
        }
      } else if (sheet.sheet_type === SheetType.ACCOUNTING) {
        queryClient.setQueryData(['accountings'], (current: Sheet[]) => [
          ...current.filter((accounting) => {
            if (accounting.id === sheet.id) {
              queryClient.removeQueries({
                queryKey: ['accountings', accounting.id, 'expenses'],
              })
              queryClient.removeQueries({
                queryKey: ['accountings', accounting.id, 'balance'],
              })
              queryClient.removeQueries({
                queryKey: ['accountings', accounting.id, 'balances'],
              })
              queryClient.removeQueries({
                queryKey: ['accountings', accounting.id, 'categories'],
              })
              return false
            }
            return true
          }),
        ])

        queryClient.invalidateQueries({ queryKey: ['budgets'] })
      }
    },
  })
}
